<template>
    <div class="container-md container-app p-0 ">
        <div class="wrapper">
            <div class="header"><h1>{{ $t('ristorante_sondaggio') }}</h1></div>
            <div class="content-full">
                <!-- User not authenticated -->
                <div v-if="!userAuthenticated">
                    <div class="title">{{ $t('sondaggio_login_title') }}</div>
                    <div class="p-3">
                        <div class="text-center">
                            <span class="material-symbols-outlined color" style="font-size: 100px;">lock</span>
                            <br>
                            <p>{{ $t('sondaggio_login_description') }}</p>
                        </div>
                    </div>
                </div>

                <!-- Loading -->
                <div v-else-if="loading" class="p-3 text-center">
                    <div class="spinner-grow" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>

                <!-- No survey available -->
                <div v-else-if="!survey">
                    <div class="title">{{ $t('ristorante_sondaggio') }}</div>
                    <div class="p-3 text-center">
                        <span class="material-symbols-outlined icone50 color">sentiment_dissatisfied</span>
                        <br>
                        <p>{{ $t('sondaggio_non_disponibile') }}</p>
                    </div>
                </div>

                <!-- Survey already completed -->
                <div class="text-center" v-else-if="isCompiled">
                    <div class="title">{{ $t('sondaggio_completato') }}</div>
                    <div class="p-3">
                        <div class="text-center">
                            <span class="material-symbols-outlined color" style="font-size: 100px;">cheer</span>
                            <br>
                        </div>
                        <p class="f22">{{ $t('sondaggio_completed_intro') }}</p>
                        <p v-if="coupon && !coupon.is_used">{{ $t('sondaggio_completed_coupon_available') }}</p>
                        <div v-if="coupon && !coupon.is_used" class="text-center color pb-4">
                            <b class="f22 text-uppercase">{{ coupon.nome }}</b>
                        </div>
                        <p v-if="coupon && !coupon.is_used">{{ $t('sondaggio_completed_coupon_hint') }}</p>
                        <p v-else-if="coupon && coupon.is_used">{{ $t('sondaggio_completed_coupon_used') }}</p>
                        <p>{{ $t('sondaggio_completed_thanks') }}</p>
                    </div>
                </div>

                <!-- Survey to complete -->
                <div v-else>
                    <div class="title">{{ $t('sondaggio_rispondi') }}</div>
                    <div class="p-3">
                        <div class="text-center">
                            <span class="material-symbols-outlined color" style="font-size: 100px;">waving_hand</span>
                            <br>
                            <p v-if="surveyDescription">{{ surveyDescription }}</p>
                        </div>

                        <div v-if="questions.length">
                            <div class="mb-3" v-for="(question, index) in questions" :key="question.id">
                                <p><b>{{ index + 1 }}. {{ getQuestionTitle(question) }}</b></p>

                                <!-- Valutazione gradimento (stelle) -->
                                <div v-if="question.tipo === 'VALUTAZIONE_GRADIMENTO'">
                                    <button
                                        type="button"
                                        class="btn btn-link p-0 mr-1"
                                        v-for="n in 5"
                                        :key="n"
                                        @click="setRating(question, n)"
                                        style="text-decoration: none;">
                                        <span
                                            class="material-symbols-outlined"
                                            :style="{fontSize: '40px', color: Number(getQuestionValue(question)) >= n ? '#f3b93f' : '#a7a6ac'}">
                                            {{ Number(getQuestionValue(question)) >= n ? 'star' : 'star_outline' }}
                                        </span>
                                    </button>
                                </div>

                                <!-- Risposta aperta -->
                                <textarea
                                    v-else-if="question.tipo === 'RISPOSTA_APERTA'"
                                    class="form-control"
                                    rows="3"
                                    :value="getQuestionValue(question)"
                                    @input="setOpenAnswer(question, $event.target.value)"
                                />

                                <!-- Risposta Si/No (toggle switch) -->
                                <div v-else-if="question.tipo === 'RISPOSTA_SI_NO'" style="display: flex; align-items: center; gap: 10px;">
                                    <span>{{ $t('no') }}</span>
                                    <label style="position: relative; display: inline-block; width: 46px; height: 24px; margin-bottom: 0;">
                                        <input
                                            type="checkbox"
                                            :checked="toBoolean(getQuestionValue(question))"
                                            @change="setYesNoAnswer(question, $event.target.checked)"
                                            style="opacity: 0; width: 0; height: 0;">
                                        <span :style="{
                                            position: 'absolute',
                                            cursor: 'pointer',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: toBoolean(getQuestionValue(question)) ? '#8bd69e' : '#ccc',
                                            transition: '.2s',
                                            borderRadius: '24px'
                                        }">
                                            <span :style="{
                                                position: 'absolute',
                                                content: '',
                                                height: '18px',
                                                width: '18px',
                                                left: '3px',
                                                bottom: '3px',
                                                backgroundColor: '#fff',
                                                transition: '.2s',
                                                borderRadius: '50%',
                                                transform: toBoolean(getQuestionValue(question)) ? 'translateX(22px)' : 'translateX(0)'
                                            }"></span>
                                        </span>
                                    </label>
                                    <span>{{ $t('yes') }}</span>
                                </div>

                                <!-- Risposta singola -->
                                <div v-else-if="question.tipo === 'RISPOSTA_SINGOLA'">
                                    <button
                                        type="button"
                                        class="btn btn-link p-0 d-flex align-items-center mb-2"
                                        v-for="answer in getAvailableAnswers(question)"
                                        :key="answer.id"
                                        @click="setSingleAnswer(question, answer.id)"
                                        style="text-decoration: none; color: inherit;">
                                        <span class="material-symbols-outlined mr-1">
                                            {{ isSingleSelected(question, answer.id) ? 'radio_button_checked' : 'radio_button_unchecked' }}
                                        </span>
                                        <span>{{ getAnswerText(answer) }}</span>
                                    </button>
                                </div>

                                <!-- Risposta multipla -->
                                <div v-else-if="question.tipo === 'RISPOSTA_MULTIPLA'">
                                    <button
                                        type="button"
                                        class="btn btn-link p-0 d-flex align-items-center mb-2"
                                        v-for="answer in getAvailableAnswers(question)"
                                        :key="answer.id"
                                        @click="toggleMultipleAnswer(question, answer.id)"
                                        style="text-decoration: none; color: inherit;">
                                        <span class="material-symbols-outlined mr-1">
                                            {{ isMultipleSelected(question, answer.id) ? 'check_box' : 'check_box_outline_blank' }}
                                        </span>
                                        <span>{{ getAnswerText(answer) }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <p v-else>{{ $t('feedback_no_questions') }}</p>

                        <button
                            type="button"
                            class="btn btn-primary"
                            :disabled="sending || loading"
                            @click="handleSave">
                            {{ sending ? $t('attendi_attimo') : $t('invia_sondaggio') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import {getSondaggioAttivo, saveSondaggioCompilato} from "../../api/account";

export default {
    name: "SurveyIndex",
    data() {
        return {
            loading: true,
            sending: false,
            survey: null,
            isCompiled: false,
            coupon: null,
            compiledSurvey: []
        };
    },
    created() {
        if (this.userAuthenticated) {
            this.fetchSurvey();
        } else {
            this.resetForGuest();
        }
    },
    computed: {
        ...mapState('localization', {
            language: state => state.language
        }),
        ...mapState('restaurant', {
            id_ristorante: state => state.id,
            locale: state => state.locale
        }),
        ...mapState('auth', {
            user: state => state
        }),
        ...mapGetters('auth', {
            userAuthenticated: 'authenticated'
        }),
        localeId: function () {
            return this.locale && this.locale.id ? this.locale.id : null;
        },
        surveyDescription: function () {
            if (!this.survey || !this.survey.traduzione_ll) return '';
            return this.survey.traduzione_ll.descrizione || '';
        },
        questions: function () {
            if (!this.survey || !Array.isArray(this.survey.sondaggio_domande)) return [];
            return [...this.survey.sondaggio_domande].sort((a, b) => {
                return Number(a.ordine || 0) - Number(b.ordine || 0);
            });
        }
    },
    watch: {
        userAuthenticated: function (authenticated) {
            if (authenticated) {
                this.fetchSurvey();
            } else {
                this.resetForGuest();
            }
        },
        language: function () {
            if (this.userAuthenticated) this.fetchSurvey();
        },
        localeId: function (newLocaleId, oldLocaleId) {
            if (!this.userAuthenticated) return;
            if (!newLocaleId || newLocaleId === oldLocaleId) return;
            this.fetchSurvey();
        }
    },
    methods: {
        resetForGuest: function () {
            this.loading = false;
            this.sending = false;
            this.survey = null;
            this.isCompiled = false;
            this.coupon = null;
            this.compiledSurvey = [];
        },
        fetchSurvey: function () {
            if (!this.user || !this.user.api_key) {
                this.resetForGuest();
                return;
            }
            if (!this.localeId) {
                this.loading = true;
                return;
            }

            this.loading = true;
            getSondaggioAttivo(this.language, {
                id_ristorante: this.id_ristorante,
                id_locale: this.localeId,
                api_key: this.user.api_key
            }).then((response) => {
                this.survey = response.survey || null;
                this.isCompiled = !!response.compiled;
                this.coupon = response.codice_sconto || null;
                this.compiledSurvey = [];

                if (this.survey && !this.isCompiled) {
                    this.prefillYesNoAnswers();
                }
            }).catch(({messages}) => {
                alert(messages.join('\n'));
            }).finally(() => {
                this.loading = false;
            });
        },
        prefillYesNoAnswers: function () {
            if (!this.survey || !Array.isArray(this.survey.sondaggio_domande)) return;

            const prefilled = [];
            this.survey.sondaggio_domande.forEach((question) => {
                if (question.tipo !== 'RISPOSTA_SI_NO') return;
                prefilled.push({
                    id_question: question.id,
                    type: question.tipo,
                    value: false
                });
            });
            this.compiledSurvey = prefilled;
        },
        toArray: function (value) {
            if (Array.isArray(value)) {
                return value.map((item) => Number(item)).filter((item) => !isNaN(item));
            }
            if (value == null || value === '') return [];
            if (typeof value === 'number') return [value];
            if (typeof value === 'string') {
                try {
                    const parsed = JSON.parse(value);
                    if (Array.isArray(parsed)) {
                        return parsed.map((item) => Number(item)).filter((item) => !isNaN(item));
                    }
                } catch (e) {}
                return value.split(',').map((item) => Number(item.trim())).filter((item) => !isNaN(item));
            }
            return [];
        },
        toBoolean: function (value) {
            return value === true || value === 1 || value === "1" || value === "true";
        },
        getQuestionTitle: function (question) {
            return question && question.traduzione_ll ? question.traduzione_ll.testo : '';
        },
        getQuestionValue: function (question) {
            const answer = this.compiledSurvey.find((item) => item.id_question === question.id);
            if (answer) return answer.value;

            switch (question.tipo) {
                case 'RISPOSTA_SI_NO':
                    return false;
                case 'VALUTAZIONE_GRADIMENTO':
                    return 0;
                case 'RISPOSTA_SINGOLA':
                case 'RISPOSTA_MULTIPLA':
                    return [];
                case 'RISPOSTA_APERTA':
                default:
                    return '';
            }
        },
        getAvailableAnswers: function (question) {
            if (!question || !Array.isArray(question.sondaggio_risposte)) return [];
            return question.sondaggio_risposte;
        },
        getAnswerText: function (answer) {
            if (!answer) return '';
            if (answer.traduzione_ll && answer.traduzione_ll.testo) return answer.traduzione_ll.testo;
            return answer.testo || '';
        },
        upsertAnswer: function (question, value) {
            const index = this.compiledSurvey.findIndex((item) => item.id_question === question.id);
            if (index >= 0) {
                const updated = [...this.compiledSurvey];
                updated[index] = {
                    ...updated[index],
                    value
                };
                this.compiledSurvey = updated;
            } else {
                this.compiledSurvey = [
                    ...this.compiledSurvey,
                    {
                        id_question: question.id,
                        type: question.tipo,
                        value
                    }
                ];
            }
        },
        setOpenAnswer: function (question, value) {
            this.upsertAnswer(question, value);
        },
        setYesNoAnswer: function (question, value) {
            this.upsertAnswer(question, !!value);
        },
        setRating: function (question, value) {
            this.upsertAnswer(question, Number(value));
        },
        setSingleAnswer: function (question, answerId) {
            this.upsertAnswer(question, [Number(answerId)]);
        },
        toggleMultipleAnswer: function (question, answerId) {
            const normalizedAnswerId = Number(answerId);
            const current = this.toArray(this.getQuestionValue(question));
            const exists = current.includes(normalizedAnswerId);
            const updated = exists
                ? current.filter((id) => id !== normalizedAnswerId)
                : [...current, normalizedAnswerId];
            this.upsertAnswer(question, updated);
        },
        isSingleSelected: function (question, answerId) {
            return this.toArray(this.getQuestionValue(question)).includes(Number(answerId));
        },
        isMultipleSelected: function (question, answerId) {
            return this.toArray(this.getQuestionValue(question)).includes(Number(answerId));
        },
        isAnswered: function (question) {
            const value = this.getQuestionValue(question);

            switch (question.tipo) {
                case 'RISPOSTA_APERTA':
                    return String(value || '').trim().length > 0;
                case 'RISPOSTA_SI_NO':
                    return value === true || value === false || value === 1 || value === 0 || value === '1' || value === '0';
                case 'VALUTAZIONE_GRADIMENTO':
                    return Number(value) > 0;
                case 'RISPOSTA_SINGOLA':
                case 'RISPOSTA_MULTIPLA':
                    return this.toArray(value).length > 0;
                default:
                    return value !== null && value !== '';
            }
        },
        isFormComplete: function () {
            return this.questions.every((question) => this.isAnswered(question));
        },
        normalizeValueForPayload: function (question) {
            const value = this.getQuestionValue(question);
            switch (question.tipo) {
                case 'RISPOSTA_SI_NO':
                    return this.toBoolean(value);
                case 'VALUTAZIONE_GRADIMENTO':
                    return Number(value) || 0;
                case 'RISPOSTA_SINGOLA':
                case 'RISPOSTA_MULTIPLA':
                    return this.toArray(value);
                case 'RISPOSTA_APERTA':
                default:
                    return String(value || '').trim();
            }
        },
        handleSave: function () {
            if (this.loading || this.sending || this.isCompiled || !this.survey) return;

            if (!this.isFormComplete()) {
                alert(this.$t('feedback_domande_incomplete'));
                return;
            }

            const compiled = this.questions.map((question) => {
                return {
                    id_question: question.id,
                    type: question.tipo,
                    value: this.normalizeValueForPayload(question)
                };
            });

            this.sending = true;
            saveSondaggioCompilato(this.language, {
                id_ristorante: this.id_ristorante,
                id_locale: this.localeId,
                api_key: this.user.api_key,
                sondaggio_compilato: compiled,
                id_sondaggio: this.survey.id
            }).then((response) => {
                this.compiledSurvey = compiled;
                this.isCompiled = !!response.compiled;
                if (response.codice_sconto) {
                    this.coupon = response.codice_sconto;
                }
            }).catch(({messages}) => {
                alert(messages.join('\n'));
            }).finally(() => {
                this.sending = false;
            });
        }
    }
}

</script>
